import { apiClient } from '@/api/axios';
import { endpoints } from '@/api/endpoints';
import type {
  AuditAction,
  AuditEvent,
  AuditExportRequest,
  AuditExportResult,
  AuditListResult,
  AuditQueryParams,
  AuditSeverity,
} from '@/audit/models';

const mockDelayMs = 140;
const defaultPageSize = 8;

const actionCatalog: AuditAction[] = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'LOGIN',
  'LOGOUT',
  'EXPORT',
  'IMPORT',
  'RESET_PASSWORD',
  'PERMISSION_CHANGE',
  'ROLE_ASSIGNED',
  'FEATURE_FLAG_UPDATED',
  'PRODUCT_DEPLOYED',
  'API_CONFIGURED',
  'MENU_UPDATED',
  'ACCESS',
];

const resourceCatalog = [
  'Users',
  'Roles',
  'Groups',
  'Products',
  'Feature Flags',
  'API Configuration',
  'Embedded Application',
  'Notification Center',
  'Audit Logs',
  'Documentation Hub',
];

const severityCatalog: AuditSeverity[] = [
  'information',
  'low',
  'medium',
  'high',
  'critical',
];

const actorCatalog = [
  { id: 'usr-1', displayName: 'Sarah Jenkins', actorType: 'user' as const },
  { id: 'usr-2', displayName: 'Alex Mercer', actorType: 'user' as const },
  { id: 'usr-3', displayName: 'David Anderson', actorType: 'user' as const },
  { id: 'sys-1', displayName: 'System Process', actorType: 'system' as const },
  {
    id: 'wrk-1',
    displayName: 'Background Worker',
    actorType: 'backgroundWorker' as const,
  },
  { id: 'sch-1', displayName: 'Scheduler', actorType: 'scheduler' as const },
];

const detailsCatalog = [
  'Updated Product Configuration',
  'Assigned Global Admin Role',
  'Removed User from Group',
  'Feature Flag Enabled',
  'Navigation Menu Updated',
  'Exported Audit Logs',
  'Applied API credential rotation',
  'Updated environment deployment policy',
];

const ipCatalog = [
  '192.168.1.14',
  '192.168.1.28',
  '10.0.4.152',
  '127.0.0.1',
  '45.22.89.102',
  '172.16.12.48',
];

const inMemoryAuditStore = new Map<string, AuditEvent>();

function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(value), mockDelayMs);
  });
}

function normalizeString(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

function seedAuditEventsIfNeeded() {
  if (inMemoryAuditStore.size > 0) {
    return;
  }

  const now = Date.now();

  for (let index = 0; index < 32; index += 1) {
    const actor = actorCatalog[index % actorCatalog.length];
    const action = actionCatalog[index % actionCatalog.length];
    const severity = severityCatalog[index % severityCatalog.length];
    const resource = resourceCatalog[index % resourceCatalog.length];
    const timestamp = new Date(now - index * 15 * 60 * 1000).toISOString();

    const item: AuditEvent = {
      id: `audit-${index + 1}`,
      timestamp,
      actor: {
        id: actor.id,
        displayName: actor.displayName,
      },
      actorType: actor.actorType,
      action,
      resource,
      resourceId: `${resource.toLowerCase().replace(/\s+/g, '-')}-${index + 1}`,
      details: detailsCatalog[index % detailsCatalog.length],
      severity,
      ipAddress: ipCatalog[index % ipCatalog.length],
      environment: index % 2 === 0 ? 'production' : 'stage',
      product: index % 3 === 0 ? 'Global Portal' : 'AML Portal',
      category:
        action === 'LOGIN' || action === 'LOGOUT'
          ? 'authentication'
          : action === 'PERMISSION_CHANGE' || action === 'ROLE_ASSIGNED'
            ? 'authorization'
            : action === 'API_CONFIGURED'
              ? 'api'
              : 'administration',
      metadata: {
        source: 'mock-fallback',
      },
      correlationId: `cor-${index + 1}`,
      sessionId: `ses-${Math.floor(index / 2) + 1}`,
    };

    inMemoryAuditStore.set(item.id, item);
  }
}

function applyFilters(
  events: AuditEvent[],
  params: AuditQueryParams,
): AuditEvent[] {
  const search = normalizeString(params.search);
  const actor = normalizeString(params.actor);
  const action = normalizeString(params.action);
  const resource = normalizeString(params.resource);
  const product = normalizeString(params.product);
  const environment = normalizeString(params.environment);
  const ipAddress = normalizeString(params.ipAddress);

  return events.filter((event) => {
    if (
      params.severity &&
      params.severity !== 'all' &&
      event.severity !== params.severity
    ) {
      return false;
    }

    if (actor && normalizeString(event.actor.displayName) !== actor) {
      return false;
    }

    if (action && normalizeString(event.action) !== action) {
      return false;
    }

    if (resource && normalizeString(event.resource) !== resource) {
      return false;
    }

    if (product && normalizeString(event.product) !== product) {
      return false;
    }

    if (environment && normalizeString(event.environment) !== environment) {
      return false;
    }

    if (ipAddress && normalizeString(event.ipAddress) !== ipAddress) {
      return false;
    }

    if (params.dateRange?.from) {
      const fromTime = new Date(params.dateRange.from).getTime();
      const eventTime = new Date(event.timestamp).getTime();
      if (eventTime < fromTime) {
        return false;
      }
    }

    if (params.dateRange?.to) {
      const toTime = new Date(params.dateRange.to).getTime();
      const eventTime = new Date(event.timestamp).getTime();
      if (eventTime > toTime) {
        return false;
      }
    }

    if (!search) {
      return true;
    }

    const text = [
      event.actor.displayName,
      event.action,
      event.resource,
      event.details,
      event.ipAddress,
      event.product,
    ]
      .join(' ')
      .toLowerCase();

    return text.includes(search);
  });
}

function applySort(
  events: AuditEvent[],
  params: AuditQueryParams,
): AuditEvent[] {
  const sortBy = params.sortBy ?? 'timestamp';
  const sortDirection = params.sortDirection ?? 'desc';

  return [...events].sort((left, right) => {
    let compare = 0;

    if (sortBy === 'timestamp') {
      compare =
        new Date(left.timestamp).getTime() -
        new Date(right.timestamp).getTime();
    }

    if (sortBy === 'actor') {
      compare = left.actor.displayName.localeCompare(right.actor.displayName);
    }

    if (sortBy === 'resource') {
      compare = left.resource.localeCompare(right.resource);
    }

    if (sortBy === 'severity') {
      compare =
        severityCatalog.indexOf(left.severity) -
        severityCatalog.indexOf(right.severity);
    }

    return sortDirection === 'asc' ? compare : -compare;
  });
}

function toCsv(items: AuditEvent[]): string {
  const headers = [
    'id',
    'timestamp',
    'actor',
    'action',
    'resource',
    'details',
    'severity',
    'ipAddress',
    'environment',
    'product',
  ];

  const rows = items.map((item) =>
    [
      item.id,
      item.timestamp,
      item.actor.displayName,
      item.action,
      item.resource,
      item.details,
      item.severity,
      item.ipAddress,
      item.environment,
      item.product,
    ]
      .map((value) => `"${String(value).replace(/"/g, '""')}"`)
      .join(','),
  );

  return [headers.join(','), ...rows].join('\n');
}

seedAuditEventsIfNeeded();

export const auditService = {
  log: async (event: Omit<AuditEvent, 'id'>): Promise<AuditEvent> => {
    const payload: AuditEvent = {
      ...event,
      id: crypto.randomUUID(),
    };

    try {
      await apiClient.post(endpoints.audit, payload);
    } catch {
      inMemoryAuditStore.set(payload.id, payload);
    }

    return payload;
  },

  query: async (params: AuditQueryParams = {}): Promise<AuditListResult> => {
    try {
      const response = await apiClient.get<AuditListResult>(endpoints.audit, {
        params,
      });

      return response.data;
    } catch {
      seedAuditEventsIfNeeded();

      const page = params.page ?? 1;
      const pageSize = params.pageSize ?? defaultPageSize;
      const all = Array.from(inMemoryAuditStore.values());
      const filtered = applySort(applyFilters(all, params), params);
      const start = (page - 1) * pageSize;

      return delay({
        items: filtered.slice(start, start + pageSize),
        total: filtered.length,
        page,
        pageSize,
      });
    }
  },

  export: async (request: AuditExportRequest): Promise<AuditExportResult> => {
    try {
      const response = await apiClient.post<AuditExportResult>(
        endpoints.auditExport,
        request,
      );
      return response.data;
    } catch {
      const result = await auditService.query({
        ...(request.filters ?? {}),
        page: 1,
        pageSize: 10_000,
      });

      return {
        format: request.format,
        fileName: `audit-trail-${Date.now()}.${request.format === 'csv' ? 'csv' : 'txt'}`,
        content:
          request.format === 'csv'
            ? toCsv(result.items)
            : JSON.stringify(result.items, null, 2),
      };
    }
  },

  getById: async (id: string): Promise<AuditEvent | null> => {
    try {
      const response = await apiClient.get<AuditEvent>(endpoints.auditById(id));
      return response.data;
    } catch {
      return delay(inMemoryAuditStore.get(id) ?? null);
    }
  },

  getActions: async (): Promise<string[]> => {
    try {
      const response = await apiClient.get<string[]>(endpoints.auditActions);
      return response.data;
    } catch {
      return delay(actionCatalog);
    }
  },

  getResources: async (): Promise<string[]> => {
    try {
      const response = await apiClient.get<string[]>(endpoints.auditResources);
      return response.data;
    } catch {
      return delay(resourceCatalog);
    }
  },

  getSeverities: async (): Promise<AuditSeverity[]> => {
    try {
      const response = await apiClient.get<AuditSeverity[]>(
        endpoints.auditSeverity,
      );
      return response.data;
    } catch {
      return delay(severityCatalog);
    }
  },
};
